import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, Box, Typography, IconButton, Button, Alert, CircularProgress, Snackbar,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { getSprintEmail } from '../../../services/sprintHealthService';
import { T } from './shared';

const emailDoc = (html) => `<!doctype html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:20px 12px;background:#F3F5F4">${html}</body></html>`;

/**
 * Copy rich HTML. The synchronous copy-event route keeps text/html and works where the async
 * Clipboard API is blocked; the async API is the fallback. Returns a promise of true/false.
 */
function copyHtml(html, text) {
  let done = false;
  const onCopy = (ev) => {
    ev.clipboardData.setData('text/html', html);
    ev.clipboardData.setData('text/plain', text);
    ev.preventDefault();
    done = true;
  };
  document.addEventListener('copy', onCopy, { once: true });
  try { document.execCommand('copy'); } catch (_) { /* fall through */ }
  document.removeEventListener('copy', onCopy);
  if (done) return Promise.resolve(true);
  if (window.ClipboardItem && navigator.clipboard?.write) {
    return navigator.clipboard.write([new window.ClipboardItem({
      'text/html': new Blob([html], { type: 'text/html' }),
      'text/plain': new Blob([text], { type: 'text/plain' }),
    })]).then(() => true, () => false);
  }
  return Promise.resolve(false);
}

/** Preview the sprint email and copy it for pasting into Outlook or Gmail. team = null → leadership version. */
const EmailPreviewDialog = ({ open, onClose, sprint, team, teamName }) => {
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState('');
  const frame = useRef(null);

  useEffect(() => {
    if (!open || !sprint) return undefined;
    let alive = true;
    setLoading(true);
    setError(null);
    setEmail(null);
    getSprintEmail(sprint, team)
      .then((res) => { if (alive) setEmail(res.data); })
      .catch((err) => { if (alive) setError(err.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, sprint, team]);

  const frameBody = () => frame.current?.contentDocument?.body;

  const selectEmail = () => {
    const doc = frame.current?.contentDocument;
    if (!doc?.body) return;
    const range = doc.createRange();
    range.selectNodeContents(doc.body);
    const sel = doc.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    frame.current.focus();
  };

  const copyEmail = async () => {
    if (!email) return;
    const ok = await copyHtml(`<html><head><meta charset="utf-8"></head><body>${email.html}</body></html>`, frameBody()?.innerText || '');
    if (ok) setToast('Copied with formatting. Paste into a new email.');
    else { selectEmail(); setToast('Copy was blocked. The email is selected: press Ctrl+C (Cmd+C on Mac).'); }
  };

  const copySubject = () => {
    if (!email) return;
    const fallback = () => copyHtml(email.subject, email.subject).then((ok) => setToast(ok ? 'Subject copied' : 'Copy was blocked'));
    if (!navigator.clipboard?.writeText) { fallback(); return; }
    navigator.clipboard.writeText(email.subject).then(() => setToast('Subject copied'), fallback);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ elevation: 8, sx: { borderRadius: '12px', height: 'calc(100% - 48px)' } }} aria-labelledby="email-preview-title">
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, px: 2.5, py: 2, borderBottom: `1px solid ${T.line}` }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography id="email-preview-title" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>Email preview · {team ? teamName : 'Leadership'}</Typography>
          <Typography variant="body2" sx={{ color: T.muted }}>
            {team ? "This team's sprint only: verdict, quarter commitments, goal, every number with its status, and notes." : 'All teams at a glance.'} Paste into a new Outlook email and add the recipients.
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close" sx={{ ml: 'auto' }}><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', px: 2.5, py: 1.25, borderBottom: `1px solid ${T.line}`, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ color: T.muted, minWidth: 60 }}>Subject</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>{email?.subject || '…'}</Typography>
        <Button size="small" variant="outlined" onClick={copySubject} disabled={!email}>Copy subject</Button>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, bgcolor: '#F3F5F4', position: 'relative' }}>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
        {email && (
          <Box
            component="iframe"
            ref={frame}
            title="Email preview"
            srcDoc={emailDoc(email.html)}
            sx={{ border: 0, width: '100%', height: '100%', display: 'block' }}
          />
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', px: 2.5, py: 1.5, borderTop: `1px solid ${T.line}`, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ color: T.muted, flex: 1, minWidth: 220 }}>
          Copy, then paste into a new Outlook or Gmail message. Formatting, colours and the link are kept.
        </Typography>
        <Button variant="outlined" onClick={selectEmail} disabled={!email}>Select email</Button>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={copyEmail} disabled={!email}>Copy to email</Button>
      </Box>
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />
    </Dialog>
  );
};

export default EmailPreviewDialog;
